import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './CalendarRangeDropdownMenu.scss';
import { BlockButton } from '@/components/BlockButton';
import { useEffect, useState, type ComponentPropsWithoutRef } from 'react';
import { clsx } from 'clsx';

type DateSelectButtonProps = {
  label: string;
  isActive: boolean;
} & ComponentPropsWithoutRef<'button'>;

const DateSelectButton = ({ label, isActive, ...props }: DateSelectButtonProps) => {
  return (
    <button
      type="button"
      {...props}
      className={clsx(
        'flex h-[42px] w-full cursor-pointer items-center px-3',
        isActive ? 'bg-primary_lighten' : 'bg-white'
      )}
    >
      <span className={clsx('text-sm', isActive && 'font-bold text-primary')}>{label}</span>
    </button>
  );
};

const renderCustomHeader = ({
  date,
  customHeaderCount,
}: {
  date: Date;
  customHeaderCount: number;
}) => {
  const adjustedDate = new Date(date);
  adjustedDate.setMonth(adjustedDate.getMonth() + customHeaderCount);

  const year = new Intl.DateTimeFormat('ja', { year: 'numeric' }).format(adjustedDate);
  const month = new Intl.DateTimeFormat('ja', { month: 'long' }).format(adjustedDate);

  return (
    <div className="flex pl-4">
      <span className="text-xs text-placeholder">{` ${year}${month} `}</span>
    </div>
  );
};

type DateSelectType =
  | 'custom'
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'past7Days'
  | 'past30Days'
  | 'past90Days'
  | 'past12Months';

type Props = {
  dateRange?: { start: Date | null; end: Date | null };
  onClose: () => void;
  setDateRange: (newRange: { start: Date; end: Date }) => void;
};

const DAY_NAMES = ['日', '月', '火', '水', '木', '金', '土'];

export default function CalendarRangeDropdownMenu({
  setDateRange,
  onClose,
  dateRange = undefined,
}: Props) {
  const [selectedType, setSelectedType] = useState<DateSelectType>('custom');
  const [selectDate, setSelectDate] = useState(dateRange);
  const [isRangeMode, setIsRangeMode] = useState(true);

  const onClickDateType = (type: DateSelectType) => {
    switch (type) {
      case 'custom':
        setSelectedType('custom');
        break;
      case 'today':
        setSelectedType('today');
        break;
      case 'yesterday':
        setSelectedType('yesterday');
        break;
      case 'thisWeek':
        setSelectedType('thisWeek');
        break;
      case 'lastWeek':
        setSelectedType('lastWeek');
        break;
      case 'past7Days':
        setSelectedType('past7Days');
        break;
      case 'past30Days':
        setSelectedType('past30Days');
        break;
      case 'past90Days':
        setSelectedType('past90Days');
        break;
      case 'past12Months':
        setSelectedType('past12Months');
        break;
    }
  };

  const onSubmit = () => {
    // 範囲選択で1日だけクリックした場合、end が null のまま親に渡ると
    // クエリの end が空になってしまうため、start と同じ日を単日範囲として補完する
    if (selectDate?.start) {
      setDateRange({ start: selectDate.start, end: selectDate.end ?? selectDate.start });
    }
    onClose();
  };

  const onClear = () => {
    setSelectedType('custom');
    setIsRangeMode(true);
    setSelectDate({ start: null, end: null });
  };

  const selectDateButton = (type: DateSelectType) => {
    onClickDateType(type);
    setSelectDate({ start: null, end: null });
  };

  useEffect(() => {
    switch (selectedType) {
      case 'custom': {
        setIsRangeMode(true);
        return;
      }
      case 'today': {
        const today = new Date();
        setIsRangeMode(false);
        setSelectDate({ start: today, end: today });
        return;
      }
      case 'yesterday': {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setIsRangeMode(false);
        setSelectDate({ start: yesterday, end: yesterday });
        return;
      }
      case 'thisWeek': {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const sunday = new Date(today);
        sunday.setDate(today.getDate() - dayOfWeek);
        setIsRangeMode(true);
        setSelectDate({ start: sunday, end: today });
        return;
      }
      case 'lastWeek': {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const sundayLastWeek = new Date(today);
        sundayLastWeek.setDate(today.getDate() - dayOfWeek - 7);
        const saturdayLastWeek = new Date(sundayLastWeek);
        saturdayLastWeek.setDate(sundayLastWeek.getDate() + 6);
        setIsRangeMode(true);
        setSelectDate({ start: sundayLastWeek, end: saturdayLastWeek });
        return;
      }
      case 'past7Days': {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        setIsRangeMode(true);
        setSelectDate({ start: sevenDaysAgo, end: today });
        return;
      }
      case 'past30Days': {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        setIsRangeMode(true);
        setSelectDate({ start: thirtyDaysAgo, end: today });
        return;
      }
      case 'past90Days': {
        const today = new Date();
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(today.getDate() - 90);
        setIsRangeMode(true);
        setSelectDate({ start: ninetyDaysAgo, end: today });
        return;
      }
      case 'past12Months': {
        const today = new Date();
        const twelveMonthsAgo = new Date(today);
        twelveMonthsAgo.setMonth(today.getMonth() - 12);
        setIsRangeMode(true);
        setSelectDate({ start: twelveMonthsAgo, end: today });
        return;
      }
    }
  }, [selectedType]);

  return (
    <div className="w-[436px] border-2 border-solid border-border bg-white">
      <div className="flex h-[361px] overflow-hidden">
        <div className="hidden-scrollbar w-[196px] overflow-y-auto border-r border-solid border-border">
          <DateSelectButton
            label="カスタム"
            isActive={selectedType === 'custom'}
            onClick={() => selectDateButton('custom')}
          />
          <DateSelectButton
            label="今日"
            isActive={selectedType === 'today'}
            onClick={() => selectDateButton('today')}
          />
          <DateSelectButton
            label="昨日"
            isActive={selectedType === 'yesterday'}
            onClick={() => selectDateButton('yesterday')}
          />
          <DateSelectButton
            label="今週（日曜日〜本日）"
            isActive={selectedType === 'thisWeek'}
            onClick={() => selectDateButton('thisWeek')}
          />
          <DateSelectButton
            label="先週（日曜日〜土曜日）"
            isActive={selectedType === 'lastWeek'}
            onClick={() => selectDateButton('lastWeek')}
          />
          <DateSelectButton
            label="過去 7日間"
            isActive={selectedType === 'past7Days'}
            onClick={() => selectDateButton('past7Days')}
          />
          <DateSelectButton
            label="過去 30日間"
            isActive={selectedType === 'past30Days'}
            onClick={() => selectDateButton('past30Days')}
          />
          <DateSelectButton
            label="過去 90日間"
            isActive={selectedType === 'past90Days'}
            onClick={() => selectDateButton('past90Days')}
          />
          <DateSelectButton
            label="過去 12ヶ月間"
            isActive={selectedType === 'past12Months'}
            onClick={() => selectDateButton('past12Months')}
          />
        </div>
        <div className="hidden-scrollbar react-datepicker-wrapper h-full overflow-y-auto">
          <div className="flex h-[33px] items-center justify-center gap-[6px] border-b border-solid border-[#E7E7E7]">
            {DAY_NAMES.map((d, key) => {
              return (
                <span
                  key={key}
                  className={clsx(
                    'flex size-[27px] items-center justify-center text-xs font-bold',
                    key === 0 && 'text-[#EA3323]',
                    key + 1 === DAY_NAMES.length && 'text-primary'
                  )}
                >
                  {d}
                </span>
              );
            })}
          </div>
          {isRangeMode ? (
            <DatePicker
              selected={selectDate?.start ?? null}
              selectsRange
              onChange={(dates) => {
                if (isRangeMode) {
                  const [start, end] = dates as [Date, Date];
                  setSelectDate({ start, end });
                }
              }}
              startDate={selectDate?.start ?? undefined}
              endDate={selectDate?.end ?? undefined}
              dateFormat="yyyy年MM月dd日"
              monthsShown={12}
              inline
              locale="ja"
              customInput={<div />}
              calendarClassName="calendar-range-dropdown-menu"
              renderCustomHeader={(params) => {
                return renderCustomHeader({
                  date: params.date,
                  customHeaderCount: params.customHeaderCount,
                });
              }}
            />
          ) : (
            <DatePicker
              key={selectDate?.start ? 'range-selected' : 'range-reset'}
              selected={selectDate?.start ?? null}
              onChange={(date) => {
                if (date === null) {
                  setSelectDate({ start: null, end: null });
                } else {
                  setSelectDate({ start: date as Date, end: date as Date });
                }
              }}
              dateFormat="yyyy年MM月dd日"
              monthsShown={12}
              inline
              locale="ja"
              customInput={<div />}
              calendarClassName="calendar-range-dropdown-menu"
              disabled
              renderCustomHeader={(params) => {
                return renderCustomHeader({
                  date: params.date,
                  customHeaderCount: params.customHeaderCount,
                });
              }}
            />
          )}
        </div>
      </div>
      <div className="flex h-[56px] w-full items-center justify-end gap-1 border-t border-solid border-border px-3">
        <div className="w-[52px]">
          <BlockButton
            type="button"
            text="クリア"
            height={32}
            colorType="white"
            onClick={onClear}
          />
        </div>
        <div className="w-xxl">
          <BlockButton type="button" text="決定" height={32} onClick={onSubmit} />
        </div>
      </div>
    </div>
  );
}
